/**
 * PKCE (Proof Key for Code Exchange) utility functions
 * Used for secure OAuth 2.0 authorization code flow
 */

/**
 * Generate a cryptographically random string for PKCE
 * @param length Length of the string to generate (default: 128)
 * @returns Base64URL-encoded random string
 */
export function generateCodeVerifier(length: number = 128): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate code challenge from code verifier using SHA256
 * @param verifier The code verifier string
 * @returns Promise resolving to base64URL-encoded SHA256 hash
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Base64URL encode (URL-safe base64 encoding)
 * @param array Uint8Array to encode
 * @returns Base64URL-encoded string
 */
function base64UrlEncode(array: Uint8Array): string {
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Base64URL decode (converts base64url to standard base64, then decodes)
 * @param base64Url Base64URL-encoded string
 * @returns Decoded string
 */
export function base64UrlDecode(base64Url: string): string {
  // Convert base64url to standard base64
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed (base64url removes padding, but atob() may need it)
  while (base64.length % 4) {
    base64 += '=';
  }

  return atob(base64);
}

/**
 * Store PKCE values in sessionStorage for later use in callback
 */
export function storePKCEValues(codeVerifier: string, codeChallenge: string) {
  sessionStorage.setItem('sso_code_verifier', codeVerifier);
  sessionStorage.setItem('sso_code_challenge', codeChallenge);
}

/**
 * Retrieve and clear PKCE code verifier from sessionStorage
 * @returns The code verifier or null if not found
 */
export function retrieveAndClearCodeVerifier(): string | null {
  const verifier = sessionStorage.getItem('sso_code_verifier');
  if (verifier) {
    sessionStorage.removeItem('sso_code_verifier');
    sessionStorage.removeItem('sso_code_challenge');
  }
  return verifier;
}
